/**
 * The life of this browser's screen session (docs/13 §2, §5.5), without React: create or recover
 * it, sign the host token in on `akp-host`, switch modes, take over from another screen, close.
 *
 * One instance per page load, outside the tree, so StrictMode and a remounted layout cannot start a
 * second session or a second engine: every entry point is idempotent and shares the call in flight.
 */
import type { FirebaseClient } from '@akpl/firebase/client';
import { AkpError, type SessionDTO } from '@akpl/shared';

import type { WebSessionMode, WebSessionResponse } from '../api';
import type { EngineEvent, PlayerEngine } from '../player/engine';

export type HostStatus = 'idle' | 'starting' | 'active';

export interface HostState {
  status: HostStatus;
  sid: string | null;
  joinCode: string | null;
  mode: WebSessionMode | null;
  client: FirebaseClient | null;
  engine: PlayerEngine | null;
  /** E-26: the mode to retry with `takeover: true`. */
  takeover: WebSessionMode | null;
  /** E-24: closed or past its 6 h. Kept until acknowledged, so the screen can say so. */
  ended: boolean;
}

export interface HostDeps {
  deviceId(): string;
  createSession(body: {
    deviceId: string;
    mode: WebSessionMode;
    takeover?: boolean;
    appVersion?: string;
  }): Promise<WebSessionResponse>;
  currentSession(deviceId: string): Promise<WebSessionResponse | null>;
  patchSession(
    sid: string,
    mode: WebSessionMode,
  ): Promise<{ session: SessionDTO; mode: WebSessionMode }>;
  signIn(customToken: string): Promise<FirebaseClient>;
  signOut(): Promise<void>;
  setTokenProvider(provider: () => Promise<string | null>): void;
  loadEngine(sid: string, client: FirebaseClient): Promise<PlayerEngine>;
  onEngineEvent(event: EngineEvent): void;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

/** Thrown to the caller of `ensure` while E-26 is up: nothing went wrong, the user decides. */
export class TakeoverRequiredError extends Error {
  override name = 'TakeoverRequiredError';
}

const INITIAL: HostState = {
  status: 'idle',
  sid: null,
  joinCode: null,
  mode: null,
  client: null,
  engine: null,
  takeover: null,
  ended: false,
};

const flagKey = (uid: string) => `akp.web.hostSession.${uid}`;

export class HostSession {
  private state: HostState = INITIAL;
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<unknown> = Promise.resolve();
  private ensuring: { mode: WebSessionMode; promise: Promise<void> } | null = null;
  private recoveredFor: string | null = null;
  private uid: string | null = null;
  /** Bumped by `shutdown`, so whatever was on its way when it happened lands on nothing. */
  private epoch = 0;

  constructor(private readonly deps: HostDeps) {}

  getState = (): HostState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<HostState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private setFlag(on: boolean): void {
    if (!this.uid) return;
    try {
      if (on) this.deps.storage?.setItem(flagKey(this.uid), '1');
      else this.deps.storage?.removeItem(flagKey(this.uid));
    } catch {
      /* private mode: a reload just starts without a session */
    }
  }

  private hasFlag(uid: string): boolean {
    try {
      return this.deps.storage?.getItem(flagKey(uid)) === '1';
    } catch {
      return false;
    }
  }

  /** Runs after whatever is in flight, in order, so a reload cannot race a click. */
  private enqueue<T>(step: () => Promise<T>): Promise<T> {
    const run = this.inFlight.then(step, step);
    this.inFlight = run.catch(() => undefined);
    return run;
  }

  /**
   * After a reload, only if this account had a session here (§8 riesgo 6: a visit to the Home must
   * not cost RTDB). A fresh token comes back; the position to resume from is in `tvState`.
   */
  recover(uid: string): Promise<void> {
    this.uid = uid;
    if (this.recoveredFor === uid) return this.inFlight.then(() => undefined);
    this.recoveredFor = uid;
    if (!this.hasFlag(uid)) return Promise.resolve();
    const epoch = this.epoch;
    return this.enqueue(async () => {
      if (this.state.sid) return;
      this.set({ status: 'starting' });
      try {
        const response = await this.deps.currentSession(this.deps.deviceId());
        if (epoch !== this.epoch) return;
        if (response) await this.adopt(response, epoch);
        else {
          this.setFlag(false);
          this.set({ status: 'idle' });
        }
      } catch {
        // Offline at boot: the flag stays, and the next action starts over.
        if (epoch === this.epoch) this.set({ status: 'idle' });
      }
    });
  }

  /** The session in this `mode`, however it has to come to exist. Safe to call from any effect. */
  ensure(mode: WebSessionMode): Promise<void> {
    if (this.ensuring?.mode === mode) return this.ensuring.promise;
    const epoch = this.epoch;
    const promise = this.enqueue(async () => {
      const { sid, mode: current } = this.state;
      if (sid && current === mode) return;
      if (sid) {
        await this.patch(sid, mode, epoch);
        return;
      }
      await this.create(mode, false, epoch);
    }).finally(() => {
      if (this.ensuring?.promise === promise) this.ensuring = null;
    });
    this.ensuring = { mode, promise };
    return promise;
  }

  private async patch(sid: string, mode: WebSessionMode, epoch: number): Promise<void> {
    const res = await this.deps.patchSession(sid, mode);
    if (epoch !== this.epoch) return;
    this.set({ mode: res.mode, joinCode: res.session.joinCode });
    const { engine } = this.state;
    engine?.setKeepAlive(res.mode === 'party');
    if (res.mode === 'party') engine?.enter();
  }

  private async create(mode: WebSessionMode, takeover: boolean, epoch: number): Promise<void> {
    this.set({ status: 'starting', takeover: null });
    try {
      const response = await this.deps.createSession({
        deviceId: this.deps.deviceId(),
        mode,
        ...(takeover ? { takeover: true } : {}),
      });
      if (epoch !== this.epoch) return;
      await this.adopt(response, epoch);
    } catch (error) {
      if (epoch !== this.epoch) return;
      this.set({ status: 'idle' });
      if (AkpError.is(error) && error.code === 'session_active_elsewhere') {
        this.set({ takeover: mode });
        throw new TakeoverRequiredError();
      }
      throw error;
    }
  }

  private async adopt(response: WebSessionResponse, epoch: number): Promise<void> {
    const client = await this.deps.signIn(response.customToken);
    if (epoch !== this.epoch) {
      await this.deps.signOut();
      return;
    }
    this.deps.setTokenProvider(() => client.getIdToken());
    this.setFlag(true);
    const { session, mode } = response;
    this.set({
      status: 'active',
      sid: session.id,
      joinCode: session.joinCode,
      mode,
      client,
      takeover: null,
      ended: false,
    });
    const engine = await this.deps.loadEngine(session.id, client);
    if (epoch !== this.epoch) {
      engine.destroy();
      return;
    }
    engine.onEvent((event) => this.deps.onEngineEvent(event));
    engine.setKeepAlive(mode === 'party');
    if (mode === 'party') engine.enter();
    this.set({ engine });
  }

  /** E-26 "Usar esta pantalla": closes the other one. */
  confirmTakeover(): Promise<void> {
    const mode = this.state.takeover;
    if (!mode) return Promise.resolve();
    const epoch = this.epoch;
    return this.enqueue(() => this.create(mode, true, epoch));
  }

  cancelTakeover(): void {
    this.set({ takeover: null });
  }

  /** E-23 confirmed: guests out, code rotated, queue cleared by the API (`PATCH`). */
  closeParty(): Promise<void> {
    const epoch = this.epoch;
    return this.enqueue(async () => {
      const { sid, engine } = this.state;
      if (!sid) return;
      engine?.leave();
      await this.patch(sid, 'solo', epoch);
    });
  }

  /** E-24: `meta` closed, the 6 h ran out, or an API call answered `session_expired`. */
  markEnded(): void {
    if (!this.state.sid || this.state.ended) return;
    this.state.engine?.destroy();
    this.setFlag(false);
    this.set({ ended: true, engine: null });
  }

  /** Forgets the session on this side: logout, or E-24 dismissed. */
  shutdown(): Promise<void> {
    this.epoch += 1;
    this.state.engine?.destroy();
    this.setFlag(false);
    this.recoveredFor = null;
    this.ensuring = null;
    this.deps.setTokenProvider(async () => null);
    const hadClient = Boolean(this.state.client);
    this.set(INITIAL);
    return hadClient ? this.deps.signOut().catch(() => undefined) : Promise.resolve();
  }
}
